<template>
  <div class="menu-import-panel">
    <!-- Zone de dépôt / sélection fichier -->
    <div
      class="drop-zone"
      :class="{ 'drop-zone--over': isDragging, 'drop-zone--loaded': !!fileContent }"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="onDrop"
      @click="$refs.fileInput.click()"
    >
      <input ref="fileInput" type="file" accept=".md,.txt" class="file-input-hidden" @change="onFileChange" />

      <template v-if="!fileContent">
        <span class="drop-icon">📄</span>
        <p class="drop-label">
          Glissez votre fichier
          <strong>.md</strong>
          ici
        </p>
        <p class="drop-hint">ou cliquez pour parcourir</p>
      </template>
      <template v-else>
        <span class="drop-icon">✅</span>
        <p class="drop-label">{{ fileName }}</p>
        <p class="drop-hint">Cliquez pour changer de fichier</p>
      </template>
    </div>

    <!-- Résultat après import -->
    <div v-if="result" class="import-result" :class="result.error ? 'result--error' : 'result--success'">
      <template v-if="result.error">
        <span class="result-icon">❌</span>
        <span>{{ result.error }}</span>
      </template>
      <template v-else>
        <span class="result-icon">✅</span>
        <span>
          <strong>{{ result.total }}</strong>
          plats importés — {{ result.created }} nouveaux, {{ result.updated }} mis à jour
        </span>
      </template>
    </div>

    <!-- Bouton importer -->
    <button class="btn-import" :disabled="!fileContent || loading" @click="doImport">
      <span v-if="loading" class="btn-spinner">⏳</span>
      <span v-else>📥 Importer les menus</span>
    </button>
  </div>
</template>

<script>
export default {
  name: "MenuImportPanel",

  props: {
    canteenId: { type: Number, default: null },
  },

  data() {
    return {
      fileContent: "",
      fileName: "",
      isDragging: false,
      loading: false,
      result: null,
    }
  },

  methods: {
    onFileChange(e) {
      const file = e.target.files[0]
      if (file) this.readFile(file)
    },

    onDrop(e) {
      this.isDragging = false
      const file = e.dataTransfer.files[0]
      if (file) this.readFile(file)
    },

    readFile(file) {
      this.result = null
      this.fileName = file.name
      const reader = new FileReader()
      reader.onload = (e) => {
        this.fileContent = e.target.result
      }
      reader.readAsText(file, "utf-8")
    },

    async doImport() {
      if (!this.fileContent || !this.canteenId) return
      this.loading = true
      this.result = null
      try {
        const csrf = this.getCsrfToken()
        const resp = await fetch(`/api/v1/canteen/${this.canteenId}/menu-import/`, {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrf,
          },
          body: JSON.stringify({ content: this.fileContent }),
        })
        const data = await resp.json()
        if (!resp.ok) {
          this.result = { error: data.detail || "Erreur lors de l'import." }
        } else {
          this.result = data
          this.$emit("imported", data)
        }
      } catch {
        this.result = { error: "Impossible de contacter le serveur." }
      } finally {
        this.loading = false
      }
    },

    getCsrfToken() {
      const match = document.cookie.match(/csrftoken=([^;]+)/)
      return match ? match[1] : ""
    },
  },
}
</script>

<style scoped>
.menu-import-panel {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.drop-zone {
  border: 2px dashed #adb5bd;
  border-radius: 10px;
  padding: 1.5rem 1rem;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  background: #f8f9fa;
}
.drop-zone:hover,
.drop-zone--over {
  border-color: #4361ee;
  background: #eef1ff;
}
.drop-zone--loaded {
  border-color: #52b788;
  background: #f0faf4;
}

.file-input-hidden {
  display: none;
}

.drop-icon {
  font-size: 2rem;
  display: block;
  margin-bottom: 0.4rem;
}
.drop-label {
  font-size: 0.9rem;
  color: #343a40;
  margin: 0 0 0.2rem;
}
.drop-hint {
  font-size: 0.75rem;
  color: #6c757d;
  margin: 0;
}

.import-result {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.9rem;
  border-radius: 8px;
  font-size: 0.85rem;
}
.result--success {
  background: #d1fae5;
  color: #065f46;
}
.result--error {
  background: #fee2e2;
  color: #991b1b;
}
.result-icon {
  flex-shrink: 0;
  font-size: 1rem;
}

.btn-import {
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  border: none;
  background: #4361ee;
  color: #fff;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, opacity 0.2s;
}
.btn-import:hover:not(:disabled) {
  background: #3a56d4;
}
.btn-import:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.btn-spinner {
  animation: spin 1s linear infinite;
  display: inline-block;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
